import { useStore } from '@/store/useStore';
import { ScanSearch, ArrowLeft, Pencil, Plus, Trash2, ArrowUp, ArrowDown, Save } from 'lucide-react';
import { useMemo, useState } from 'react';
import { nanoid } from 'nanoid';
import { okkConfig, type OkkQuestion, type OkkQuestionsConfig } from '@/data/okkForm';
import { usePerms } from '@/auth/usePerms';

// Список форм (пока единственная — «Форма ОКК»). По действию открывается таблица ответов.
const FORMS = [
  { id: 1, name: 'Форма ОКК', created_at: '2024-06-29T15:25:20', updated_at: '2024-07-02T11:52:45' },
];

export function FormsPage() {
  const { deals } = useStore();
  const okkQuestions = useStore((s) => s.settings.okk_questions);
  const perms = usePerms();
  const isSuper = useStore((s) => s.currentUser?.access_role === 'superadmin');
  const canEdit = perms.deals.okk_edit || isSuper;

  const [openForm, setOpenForm] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const fmt = (s: string) => s.replace('T', ' ').slice(0, 16);

  // Актуальная анкета — из настроек (руководитель мог её править), иначе дефолт-хардкод.
  const cfg = okkConfig(okkQuestions);
  const allQ = useMemo(() => [...cfg.required, ...cfg.optional], [cfg]);

  // Игры с заполненной анкетой ОКК — строки таблицы ответов.
  const responses = useMemo(
    () => deals.filter((d) => d.okk && Object.keys(d.okk.answers).length > 0)
      .sort((a, b) => (b.okk!.updated_at ?? 0) - (a.okk!.updated_at ?? 0)),
    [deals],
  );

  if (editing) {
    return <OkkFormEditor initial={cfg} onClose={() => setEditing(false)} />;
  }

  if (openForm != null) {
    return (
      <div className="h-full overflow-auto bg-surface-0">
        <div className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <button className="btn-soft !h-9" onClick={() => setOpenForm(null)}><ArrowLeft className="w-4 h-4" /> Назад</button>
            <h1 className="text-lg font-bold text-ink-1">Форма ОКК — ответы <span className="text-ink-3 font-normal">({responses.length})</span></h1>
          </div>
          <div className="panel overflow-hidden">
            <div className="overflow-x-auto">
              <table className="text-sm">
                <thead>
                  <tr className="text-left text-xs text-ink-3 border-b border-line bg-surface-2/40">
                    <th className="px-3 py-3 sticky left-0 bg-surface-2 z-10 w-[120px] font-medium">Игра</th>
                    <th className="px-3 py-3 w-[150px] font-medium">Дата создания</th>
                    <th className="px-3 py-3 w-[150px] font-medium">Дата обновления</th>
                    {allQ.map((q) => (
                      <th key={q.id} className="px-3 py-3 font-medium min-w-[180px] max-w-[240px] align-top" title={q.q}>
                        <span className="line-clamp-3">{q.q}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {responses.length === 0 && (
                    <tr><td colSpan={3 + allQ.length} className="px-3 py-10 text-center text-ink-3">Ответов пока нет.</td></tr>
                  )}
                  {responses.map((d) => (
                    <tr key={d.id} className="border-b border-line/60 hover:bg-surface-2/40">
                      <td className="px-3 py-2.5 sticky left-0 bg-surface-1 z-10 font-medium text-accent">Игра #{d.number}</td>
                      <td className="px-3 py-2.5 text-ink-3 tabular-nums whitespace-nowrap">{d.okk!.created_at ? new Date(d.okk!.created_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      <td className="px-3 py-2.5 text-ink-3 tabular-nums whitespace-nowrap">{d.okk!.updated_at ? new Date(d.okk!.updated_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      {allQ.map((q) => (
                        <td key={q.id} className="px-3 py-2.5 text-ink-1 align-top min-w-[180px] max-w-[240px]">{d.okk!.answers[q.id] || '—'}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-surface-0">
      <div className="max-w-[1400px] mx-auto p-3 sm:p-5">
        <div className="panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-ink-3 border-b border-line bg-surface-2/40">
                  <th className="px-3 py-3 w-10"><input type="checkbox" className="w-4 h-4 accent-accent" /></th>
                  <th className="px-3 py-3 w-[90px] font-medium">#</th>
                  <th className="px-3 py-3 font-medium">Название</th>
                  <th className="px-3 py-3 w-[200px] font-medium">Дата создания</th>
                  <th className="px-3 py-3 w-[200px] font-medium">Дата изменения</th>
                  <th className="px-3 py-3 w-[120px] font-medium text-center">Действия</th>
                </tr>
              </thead>
              <tbody>
                {FORMS.map((f) => (
                  <tr key={f.id} className="border-b border-line/60 hover:bg-surface-2/40 transition-colors">
                    <td className="px-3 py-3"><input type="checkbox" className="w-4 h-4 accent-accent" /></td>
                    <td className="px-3 py-3"><span className="text-accent font-medium">#{f.id}</span></td>
                    <td className="px-3 py-3 text-ink-1">{f.name}</td>
                    <td className="px-3 py-3 text-ink-3 tabular-nums">{fmt(f.created_at)}</td>
                    <td className="px-3 py-3 text-ink-3 tabular-nums">{okkQuestions?.updated_at
                      ? new Date(okkQuestions.updated_at).toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
                      : fmt(f.updated_at)}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-center gap-1">
                        {canEdit && (
                          <button className="icon-btn !w-8 !h-8 text-ink-3 hover:text-accent" title="Редактировать вопросы анкеты" onClick={() => setEditing(true)}>
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                        <button className="icon-btn !w-8 !h-8 text-ink-3 hover:text-accent" title="Ответы по форме" onClick={() => setOpenForm(f.id)}>
                          <ScanSearch className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-end gap-4 p-4 text-xs text-ink-3">
            <span>Элементов на странице <span className="font-semibold text-ink-2">100</span></span>
            <span className="tabular-nums">1–{FORMS.length} из {FORMS.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Конструктор анкеты ОКК (доступен роли «Руководитель» + супер-админу) ──────────
// Правит вопросы в рантайме: добавить/удалить/переставить/сменить тип/варианты ответа,
// перенести между обяз./необяз., изменить инструкции. Сохраняется в настройки приложения.
function OkkFormEditor({ initial, onClose }: { initial: OkkQuestionsConfig; onClose: () => void }) {
  const patchSettings = useStore((s) => s.patchSettings);
  const toast = useStore((s) => s.toast);
  // Глубокая копия, чтобы правки не мутировали исходные объекты настроек.
  const [draft, setDraft] = useState<OkkQuestionsConfig>(() => structuredClone(initial));
  const [saving, setSaving] = useState(false);

  type Bucket = 'required' | 'optional';

  const patchQ = (bucket: Bucket, idx: number, patch: Partial<OkkQuestion>) => {
    setDraft((d) => {
      const list = d[bucket].map((q, i) => (i === idx ? { ...q, ...patch } : q));
      return { ...d, [bucket]: list };
    });
  };

  const removeQ = (bucket: Bucket, idx: number) => {
    if (!window.confirm('Удалить этот вопрос? Ранее сохранённые ответы на него перестанут отображаться.')) return;
    setDraft((d) => ({ ...d, [bucket]: d[bucket].filter((_, i) => i !== idx) }));
  };

  const moveQ = (bucket: Bucket, idx: number, dir: -1 | 1) => {
    setDraft((d) => {
      const list = [...d[bucket]];
      const j = idx + dir;
      if (j < 0 || j >= list.length) return d;
      [list[idx], list[j]] = [list[j], list[idx]];
      return { ...d, [bucket]: list };
    });
  };

  // Перенос вопроса между «обязательными» и «необязательными» (id сохраняется).
  const switchBucket = (bucket: Bucket, idx: number) => {
    const other: Bucket = bucket === 'required' ? 'optional' : 'required';
    setDraft((d) => {
      const q = d[bucket][idx];
      return { ...d, [bucket]: d[bucket].filter((_, i) => i !== idx), [other]: [...d[other], q] };
    });
  };

  const addQ = (bucket: Bucket) => {
    const q: OkkQuestion = { id: `okk_${nanoid(8)}`, q: 'Новый вопрос', type: 'text' };
    setDraft((d) => ({ ...d, [bucket]: [...d[bucket], q] }));
  };

  const patchOptions = (bucket: Bucket, idx: number, options: string[]) => patchQ(bucket, idx, { options });

  const addInstruction = () => setDraft((d) => ({ ...d, instructions: [...d.instructions, ''] }));
  const patchInstruction = (i: number, v: string) => setDraft((d) => ({ ...d, instructions: d.instructions.map((t, k) => (k === i ? v : t)) }));
  const removeInstruction = (i: number) => setDraft((d) => ({ ...d, instructions: d.instructions.filter((_, k) => k !== i) }));

  const save = async () => {
    // Убираем пустые инструкции; чистим варианты у не-radio вопросов.
    const clean: OkkQuestionsConfig = {
      required: draft.required.map(normalizeQ),
      optional: draft.optional.map(normalizeQ),
      instructions: draft.instructions.map((t) => t.trim()).filter(Boolean),
      updated_at: Date.now(),
    };
    setSaving(true);
    try {
      await patchSettings({ okk_questions: clean });
      toast({ kind: 'success', text: 'Анкета ОКК сохранена' });
      onClose();
    } catch (e) {
      toast({ kind: 'error', text: `Не удалось сохранить (${e instanceof Error ? e.message : e})` });
    } finally {
      setSaving(false);
    }
  };

  const renderBucket = (bucket: Bucket, title: string) => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-bold text-ink-1">{title}</h2>
        <span className="text-xs text-ink-3">({draft[bucket].length})</span>
        <div className="flex-1" />
        <button className="btn-soft !h-8 text-xs" onClick={() => addQ(bucket)}><Plus className="w-4 h-4" /> Добавить вопрос</button>
      </div>
      {draft[bucket].length === 0 && <div className="text-sm text-ink-3 px-1">Вопросов нет.</div>}
      {draft[bucket].map((q, i) => (
        <QuestionCard
          key={q.id}
          q={q} num={i + 1} bucket={bucket}
          first={i === 0} last={i === draft[bucket].length - 1}
          onPatch={(p) => patchQ(bucket, i, p)}
          onOptions={(opts) => patchOptions(bucket, i, opts)}
          onMove={(dir) => moveQ(bucket, i, dir)}
          onSwitch={() => switchBucket(bucket, i)}
          onRemove={() => removeQ(bucket, i)}
        />
      ))}
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-surface-0">
      <div className="h-12 px-4 flex items-center gap-3 border-b border-line bg-surface-1 flex-shrink-0">
        <button className="icon-btn" onClick={onClose} title="Назад к формам"><ArrowLeft className="w-4 h-4" /></button>
        <h1 className="text-base font-semibold flex items-center gap-2"><Pencil className="w-4 h-4 text-accent" /> Форма ОКК — редактирование</h1>
        <div className="flex-1" />
        <button className="btn-primary !h-8 text-xs" onClick={save} disabled={saving}>
          <Save className="w-4 h-4" /> {saving ? 'Сохранение…' : 'Сохранить'}
        </button>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="max-w-[900px] mx-auto p-5 space-y-6">
          {/* Инструкции */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-bold text-ink-1">Инструкции</h2>
              <div className="flex-1" />
              <button className="btn-soft !h-8 text-xs" onClick={addInstruction}><Plus className="w-4 h-4" /> Добавить</button>
            </div>
            {draft.instructions.map((t, i) => (
              <div key={i} className="flex items-start gap-2">
                <textarea
                  className="input !h-auto min-h-[64px] py-2 resize-y flex-1 whitespace-pre-wrap"
                  value={t} onChange={(e) => patchInstruction(i, e.target.value)}
                  placeholder="Текст инструкции"
                />
                <button className="icon-btn text-status-refused" title="Удалить" onClick={() => removeInstruction(i)}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {draft.instructions.length === 0 && <div className="text-sm text-ink-3 px-1">Инструкций нет.</div>}
          </div>

          {renderBucket('required', '1. Обязательные вопросы')}
          {renderBucket('optional', '2. Необязательные вопросы')}
        </div>
      </div>
    </div>
  );
}

// Приводит вопрос к консистентному виду перед сохранением.
function normalizeQ(q: OkkQuestion): OkkQuestion {
  const out: OkkQuestion = { id: q.id, q: q.q.trim() || 'Вопрос', type: q.type };
  if (q.type === 'radio') out.options = (q.options ?? []).map((o) => o.trim()).filter(Boolean);
  if (q.hint && q.hint.trim()) out.hint = q.hint.trim();
  return out;
}

function QuestionCard({ q, num, bucket, first, last, onPatch, onOptions, onMove, onSwitch, onRemove }: {
  q: OkkQuestion; num: number; bucket: 'required' | 'optional';
  first: boolean; last: boolean;
  onPatch: (p: Partial<OkkQuestion>) => void;
  onOptions: (opts: string[]) => void;
  onMove: (dir: -1 | 1) => void;
  onSwitch: () => void;
  onRemove: () => void;
}) {
  const options = q.options ?? [];
  const setOption = (i: number, v: string) => onOptions(options.map((o, k) => (k === i ? v : o)));
  const addOption = () => onOptions([...options, '']);
  const removeOption = (i: number) => onOptions(options.filter((_, k) => k !== i));

  return (
    <div className="panel p-3 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-xs text-ink-3 font-medium w-6">{bucket === 'required' ? '1' : '2'}.{num}</span>
        <input
          className="input flex-1"
          value={q.q} onChange={(e) => onPatch({ q: e.target.value })}
          placeholder="Текст вопроса"
        />
        <button className="icon-btn" title="Выше" disabled={first} onClick={() => onMove(-1)}><ArrowUp className="w-4 h-4" /></button>
        <button className="icon-btn" title="Ниже" disabled={last} onClick={() => onMove(1)}><ArrowDown className="w-4 h-4" /></button>
        <button className="icon-btn text-status-refused" title="Удалить вопрос" onClick={onRemove}><Trash2 className="w-4 h-4" /></button>
      </div>

      <div className="flex items-center gap-3 flex-wrap pl-8">
        <label className="flex items-center gap-2 text-xs text-ink-2">
          Тип ответа
          <select className="input !h-8 !w-40" value={q.type} onChange={(e) => onPatch({ type: e.target.value as OkkQuestion['type'] })}>
            <option value="radio">Выбор из вариантов</option>
            <option value="text">Свободный текст</option>
            <option value="rating">Оценка 1–10</option>
          </select>
        </label>
        <button className="btn-soft !h-8 text-xs" onClick={onSwitch}>
          {bucket === 'required' ? 'Сделать необязательным' : 'Сделать обязательным'}
        </button>
      </div>

      {q.type === 'radio' && (
        <div className="pl-8 space-y-1.5">
          <div className="text-xs text-ink-3">Варианты ответа</div>
          {options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className="input !h-8 flex-1" value={o} onChange={(e) => setOption(i, e.target.value)} placeholder="Вариант" />
              <button className="icon-btn !w-8 !h-8 text-status-refused" title="Убрать вариант" onClick={() => removeOption(i)}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <button className="btn-soft !h-8 text-xs" onClick={addOption}><Plus className="w-4 h-4" /> Вариант</button>
        </div>
      )}

      <div className="pl-8">
        <input
          className="input !h-8"
          value={q.hint ?? ''} onChange={(e) => onPatch({ hint: e.target.value })}
          placeholder="Подсказка под вопросом (необязательно)"
        />
      </div>
    </div>
  );
}
